import { ReactNode } from "react";
import { Box, Container, Flex, Text, LegacyText } from "flicket-ui";
import { ContentContainer, Footer, Icon, Layout } from "~components/common";
import {
  ExtendedFile,
  VenueAddressFragment,
  VenueInfoFragment,
} from "~graphql/sdk";
import Image from "~components/common/Image";
import EventHeader from "~components/common/common.EventHeader";

interface PageLayoutProps {
  type: "membership" | "event";
  title: string;
  description?: string;
  supportInfo?: string;
  accessibility?: string;
  startDate?: string;
  endDate?: string;
  venue?: VenueInfoFragment & VenueAddressFragment;
  location?: string;
  coordinates?: string;
  bannerImage?: ExtendedFile | null;
  backgroundImage?: ExtendedFile | null;
  warning?: string;
  children: ReactNode;
  goBack?: () => void;
}

export const PageLayout = ({
  type,
  title,
  description,
  supportInfo,
  accessibility,
  startDate,
  endDate,
  venue,
  location,
  coordinates,
  bannerImage,
  backgroundImage,
  warning,
  children,
  goBack,
}: PageLayoutProps) => {
  return (
    <>
      <Flex
        flexDirection="column"
        flex={1}
        maxH={{ _: "calc(100vh - 73px)", md: "unset" }}
        overflowY={{ _: "auto", md: "visible" }}
      >
        <Box d={{ _: "block", md: "none" }} pb={8}>
          <EventHeader
            type={type}
            title={title}
            startDate={startDate}
            endDate={endDate}
            venue={venue}
            location={location}
            accessibility={accessibility}
            image={bannerImage}
            description={description}
            supportInfo={supportInfo}
            goBack={goBack}
            coordinates={coordinates}
          />
        </Box>
        <Layout layoutType="overlay">
          <ContentContainer
            variant="content"
            image={backgroundImage}
            innerProps={{
              pt: { _: 0, md: 4 },
            }}
          >
            <Container
              mb={4}
              innerProps={{
                minHeight: { _: 0, md: "320px" },
                pb: { _: 10, md: 4 },
                pt: bannerImage ? { md: 0 } : { md: 4 },
                overflow: { _: "visible", md: "hidden" },
                borderRadius: { _: "none", md: "lg" },
              }}
            >
              {bannerImage && (
                <Image
                  image={bannerImage}
                  alt={`${title} banner`}
                  priority={true}
                  // @ts-expect-error override
                  m={{ _: "0 -32px 16px -32px" }}
                  // Offset by the negative margin + the 16px padding of the container
                  // This ensures we don't get any horizontal scroll overflow
                  // TODO: This probably needs something less magic number-y
                  maxWidth={{
                    _: "calc(100% + 48px)",
                    md: "unset",
                  }}
                />
              )}
              {warning && (
                <Flex
                  p={2}
                  w="100%"
                  alignItems="center"
                  borderRadius="sm"
                  bg="N100"
                  border="1px"
                  borderColor="N400"
                  my={3}
                >
                  <Icon fontSize={6} icon="info" color="warning" mr={1} />
                  <LegacyText fontSize={3} lineHeight="high" color="N800">
                    {warning}
                  </LegacyText>
                </Flex>
              )}
              {children}
            </Container>
            <Footer />
          </ContentContainer>
        </Layout>
      </Flex>
    </>
  );
};
